import { useState, useMemo } from 'react';
import { useDeviceSearch, useDeviceDelete, useAction } from '../../hooks/useActions';
import type { DeviceSearchResponse, DeviceSearchResult, DeviceDeleteResponse } from '../../api/actions';

interface DeviceSearchPanelProps {
  subscriptionId: string;
}

const MAC_REGEX = /^([0-9a-f]{2}:){5}[0-9a-f]{2}$/;
const MAX_MACS = 5;

function parseMacInput(input: string): string[] {
  return input
    .split(/[\n,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}

function validateMacs(macs: string[]): string | null {
  if (macs.length === 0) return 'Enter at least one MAC address.';
  if (macs.length > MAX_MACS) return `Maximum ${MAX_MACS} MAC addresses allowed.`;
  const invalid = macs.filter((m) => !MAC_REGEX.test(m));
  if (invalid.length > 0) return `Invalid MAC format: ${invalid.join(', ')}`;
  const unique = new Set(macs);
  if (unique.size !== macs.length) return 'Duplicate MAC addresses found.';
  return null;
}

function safeParseJson<T>(json: string | null | undefined): T | null {
  if (!json) return null;
  try { return JSON.parse(json) as T; }
  catch { return null; }
}

const ACTIVE_STATUSES = ['pending', 'claimed', 'running'];

export default function DeviceSearchPanel({ subscriptionId }: DeviceSearchPanelProps) {
  // User-driven state
  const [macInput, setMacInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedMacs, setSelectedMacs] = useState<Set<string>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);

  // Action IDs — set by event handlers, cleared by reset
  const [searchActionId, setSearchActionId] = useState<string | null>(null);
  const [deleteActionId, setDeleteActionId] = useState<string | null>(null);

  // Hooks for action polling
  const deviceSearch = useDeviceSearch(subscriptionId);
  const deviceDelete = useDeviceDelete(subscriptionId);
  const { data: searchAction } = useAction(searchActionId);
  const { data: deleteAction } = useAction(deleteActionId);

  // Derive reboot action ID from delete result (no state needed)
  const deleteResult = useMemo(
    () => deleteAction?.status === 'completed' ? safeParseJson<DeviceDeleteResponse>(deleteAction.result) : null,
    [deleteAction]
  );
  const rebootActionId = deleteResult?.reboot_action_id ?? null;
  const { data: rebootAction } = useAction(rebootActionId);

  // Derive search results from search action (no state needed)
  const searchResult = useMemo(
    () => searchAction?.status === 'completed' ? safeParseJson<DeviceSearchResponse>(searchAction.result) : null,
    [searchAction]
  );
  const devices: DeviceSearchResult[] = searchResult?.devices ?? [];
  const deletedCount = deleteResult?.deleted_count ?? 0;

  // --- Event handlers ---

  const handleSearch = async () => {
    const macs = parseMacInput(macInput);
    const error = validateMacs(macs);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setDeleteActionId(null);
    setSelectedMacs(new Set());
    setShowConfirm(false);
    try {
      const action = await deviceSearch.search(macs);
      setSearchActionId(action.id);
    } catch (err) {
      console.error('Failed to create device search action:', err);
    }
  };

  const handleDelete = async () => {
    setShowConfirm(false);
    try {
      const action = await deviceDelete.deleteDevices(Array.from(selectedMacs));
      setDeleteActionId(action.id);
    } catch (err) {
      console.error('Failed to create device delete action:', err);
    }
  };

  const handleToggleDevice = (mac: string) => {
    setSelectedMacs((prev) => {
      const next = new Set(prev);
      if (next.has(mac)) next.delete(mac);
      else next.add(mac);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedMacs.size === devices.length) setSelectedMacs(new Set());
    else setSelectedMacs(new Set(devices.map((d) => d.mac)));
  };

  const handleReset = () => {
    setMacInput('');
    setValidationError(null);
    setSearchActionId(null);
    setDeleteActionId(null);
    setSelectedMacs(new Set());
    setShowConfirm(false);
  };

  // --- Derive display from action statuses (no useEffect) ---

  // Reboot in progress or completed/failed
  if (rebootActionId && rebootAction) {
    if (ACTIVE_STATUSES.includes(rebootAction.status)) {
      return (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <div>
              <p className="font-medium">
                {deletedCount} device{deletedCount !== 1 ? 's' : ''} deleted. Restarting controller...
              </p>
              <p className="text-sm text-gray-500">This may take up to 5 minutes.</p>
            </div>
          </div>
        </div>
      );
    }
    if (rebootAction.status === 'completed') {
      return (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-medium text-green-700">
                {deletedCount} device{deletedCount !== 1 ? 's' : ''} deleted. Controller is restarting.
              </p>
            </div>
            <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Search Again
            </button>
          </div>
        </div>
      );
    }
    if (rebootAction.status === 'failed') {
      return (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-sm text-red-700">Devices deleted but restart failed — use Restart Service below.</p>
            </div>
            <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Search Again
            </button>
          </div>
        </div>
      );
    }
  }

  // Delete completed (no reboot chained)
  if (deleteAction?.status === 'completed' && !rebootActionId) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-medium text-green-700">
              {deletedCount} device{deletedCount !== 1 ? 's' : ''} deleted.
            </p>
          </div>
          <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Search Again
          </button>
        </div>
      </div>
    );
  }

  // Delete in progress
  if (deleteActionId && deleteAction && ACTIVE_STATUSES.includes(deleteAction.status)) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600" />
          <div>
            <p className="font-medium">Deleting devices...</p>
            <p className="text-sm text-gray-500">This may take up to 5 minutes.</p>
          </div>
        </div>
      </div>
    );
  }

  // Delete failed
  if (deleteAction?.status === 'failed') {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-red-700">{deleteAction.result || 'Device deletion failed.'}</p>
          </div>
          <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Confirm delete
  if (showConfirm) {
    const selectedDevices = devices.filter((d) => selectedMacs.has(d.mac));
    return (
      <div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-amber-800 mb-2">
            Delete {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''}?
          </h4>
          <ul className="text-sm text-amber-700 space-y-1 mb-3">
            {selectedDevices.map((d) => (
              <li key={d.mac} className="font-mono">
                {d.mac} {d.name ? `(${d.name})` : ''}
              </li>
            ))}
          </ul>
          <p className="text-sm text-amber-700">
            Deleting devices will require a brief UniFi Network Application restart.
            Deleted devices must be in a factory default state to be re-adopted.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDelete}
            disabled={deviceDelete.isPending}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deviceDelete.isPending ? 'Starting...' : 'Delete & Restart'}
          </button>
          <button
            onClick={() => setShowConfirm(false)}
            className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Search in progress
  if (searchActionId && searchAction && ACTIVE_STATUSES.includes(searchAction.status)) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
          <div>
            <p className="font-medium">Searching for devices...</p>
            <p className="text-sm text-gray-500">This may take up to 5 minutes.</p>
          </div>
        </div>
      </div>
    );
  }

  // Search failed
  if (searchAction?.status === 'failed') {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-red-700">{searchAction.result || 'Device search failed.'}</p>
          </div>
          <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Search completed — show results
  if (searchAction?.status === 'completed') {
    if (devices.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <p className="text-sm text-gray-600">No devices found matching the provided MAC addresses.</p>
          <button onClick={handleReset} className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium">
            Search Again
          </button>
        </div>
      );
    }

    return (
      <div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selectedMacs.size === devices.length}
                    onChange={handleToggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MAC Address</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Site</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {devices.map((device) => (
                <tr key={device.mac} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedMacs.has(device.mac)}
                      onChange={() => handleToggleDevice(device.mac)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-900">{device.mac}</td>
                  <td className="px-3 py-2 text-gray-900">{device.name || '-'}</td>
                  <td className="px-3 py-2 text-gray-500">{device.model}</td>
                  <td className="px-3 py-2 text-gray-500">{device.site_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <button
            onClick={() => setShowConfirm(true)}
            disabled={selectedMacs.size === 0}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete Selected ({selectedMacs.size})
          </button>
          <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Search Again
          </button>
        </div>
      </div>
    );
  }

  // Default: input
  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        Search for devices by MAC address to verify adoption or remove stale entries.
      </p>
      <textarea
        value={macInput}
        onChange={(e) => { setMacInput(e.target.value); setValidationError(null); }}
        placeholder="aa:bb:cc:dd:ee:ff&#10;11:22:33:44:55:66"
        rows={3}
        className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-blue-500"
      />
      <p className="text-xs text-gray-400 mt-1">
        One MAC per line or comma-separated. Format: aa:bb:cc:dd:ee:ff (max {MAX_MACS})
      </p>
      {validationError && <p className="mt-2 text-sm text-red-600">{validationError}</p>}
      <button
        onClick={handleSearch}
        disabled={deviceSearch.isPending || macInput.trim().length === 0}
        className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {deviceSearch.isPending ? 'Starting...' : 'Search'}
      </button>
      {deviceSearch.error && (
        <p className="mt-2 text-sm text-red-600">
          {(() => {
            const error = deviceSearch.error as { response?: { data?: { message?: string } } };
            return error?.response?.data?.message || (deviceSearch.error as Error)?.message || 'Failed to start search';
          })()}
        </p>
      )}
    </div>
  );
}
